import { type FormEvent, useRef, useState } from "react";
import type { WorkforceSupabaseSession } from "./supabaseClient";
import type { WorkforceAuthLink } from "./workforceAuthLink";

const invalidLinkMessage =
  "This link has expired, has already been used, or is invalid. Request a new password setup email below.";

/** This component never mounts clinical children, including during auth state change events. */
export function WorkforceAccountSetup({
  workforce,
  link,
  onDone,
}: {
  readonly workforce: WorkforceSupabaseSession;
  readonly link: WorkforceAuthLink | null;
  readonly onDone: () => void;
}) {
  const [stage, setStage] = useState<"confirm" | "request" | "totp" | "password" | "saved">(
    link === null || link.kind === "invalid" ? "request" : "confirm",
  );
  const [error, setError] = useState<string | null>(
    link?.kind === "invalid" ? invalidLinkMessage : null,
  );
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const working = useRef(false);
  const verifiedUserId = useRef<string | null>(null);

  const run = async (action: () => Promise<void>) => {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch {
      setError("The request could not be completed. Please try again.");
    } finally {
      working.current = false;
      setBusy(false);
    }
  };

  const requireVerifiedUser = async () => {
    const result = await workforce.client.auth.getUser();
    if (
      result.error ||
      !result.data.user ||
      (verifiedUserId.current !== null && verifiedUserId.current !== result.data.user.id)
    ) {
      throw new Error("Account session is no longer valid");
    }
    return result.data.user;
  };

  const confirmLink = () =>
    run(async () => {
      if (link === null || link.kind === "invalid") return;
      // A previously signed-in admin must never become the password-update target.
      await workforce.clear();
      const result =
        link.kind === "token"
          ? await workforce.client.auth.verifyOtp({ token_hash: link.tokenHash, type: link.type })
          : await workforce.client.auth.setSession({
              access_token: link.accessToken,
              refresh_token: link.refreshToken,
            });
      if (result.error || !result.data.session) {
        await workforce.clear();
        setStage("request");
        setError(invalidLinkMessage);
        return;
      }
      const user = await requireVerifiedUser();
      verifiedUserId.current = user.id;
      setVerifiedEmail(user.email ?? "");
      const assurance = await workforce.client.auth.mfa.getAuthenticatorAssuranceLevel();
      if (assurance.error || !assurance.data?.currentLevel)
        throw new Error("Assurance unavailable");
      setStage(
        assurance.data.nextLevel === "aal2" && assurance.data.currentLevel !== "aal2"
          ? "totp"
          : "password",
      );
    });

  const verifyAuthenticator = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      await requireVerifiedUser();
      const factors = await workforce.client.auth.mfa.listFactors();
      const factor = factors.data?.totp.find((candidate) => candidate.status === "verified");
      if (factors.error || !factor) throw new Error("Verified authenticator required");
      const challenge = await workforce.client.auth.mfa.challenge({ factorId: factor.id });
      if (challenge.error) throw new Error("Challenge failed");
      const verified = await workforce.client.auth.mfa.verify({
        factorId: factor.id,
        challengeId: challenge.data.id,
        code,
      });
      setCode("");
      if (verified.error) {
        setError("Authenticator code was not accepted.");
        return;
      }
      const assurance = await workforce.client.auth.mfa.getAuthenticatorAssuranceLevel();
      if (assurance.error || assurance.data?.currentLevel !== "aal2")
        throw new Error("AAL2 required");
      setStage("password");
    });
  };

  const savePassword = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      if (password !== confirmation) {
        setError("The passwords do not match.");
        return;
      }
      if (
        password.length < 12 ||
        !/[a-z]/.test(password) ||
        !/[A-Z]/.test(password) ||
        !/[0-9]/.test(password) ||
        !/[^a-zA-Z0-9\s]/.test(password)
      ) {
        setError(
          "Use at least 12 characters with uppercase and lowercase letters, a number, and a symbol.",
        );
        return;
      }
      await requireVerifiedUser();
      const assurance = await workforce.client.auth.mfa.getAuthenticatorAssuranceLevel();
      if (assurance.error || !assurance.data?.currentLevel)
        throw new Error("Assurance unavailable");
      if (assurance.data.nextLevel === "aal2" && assurance.data.currentLevel !== "aal2") {
        setStage("totp");
        return;
      }
      try {
        const result = await workforce.client.auth.updateUser({ password });
        if (result.error) {
          setError(
            "Password could not be saved. Use a strong, different password or request a new setup email.",
          );
          return;
        }
        await workforce.clear();
        verifiedUserId.current = null;
        setStage("saved");
      } finally {
        setPassword("");
        setConfirmation("");
      }
    });
  };

  const requestRecovery = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      setNotice(null);
      // Templates use TokenHash and do not depend on a PKCE verifier surviving a new tab.
      const result = await workforce.client.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: new URL("/", window.location.origin).href,
      });
      if (result.error) {
        setError("A setup email could not be requested. Please wait and try again.");
        return;
      }
      setNotice(
        "If this email belongs to an invited workforce account, a password setup email is on its way. Open only the newest email.",
      );
    });
  };

  const returnToSignIn = () =>
    run(async () => {
      await workforce.clear();
      onDone();
    });

  const title =
    stage === "confirm"
      ? "Continue account setup"
      : stage === "totp"
        ? "Verify your authenticator"
        : stage === "password"
          ? "Set your password"
          : stage === "saved"
            ? "Password saved"
            : "Get a password setup email";
  return (
    <main className="workforce-gate" aria-labelledby="account-setup-title">
      <section>
        <p className="eyebrow">Protected clinical workspace</p>
        <h1 id="account-setup-title">{title}</h1>
        {stage === "confirm" ? (
          <>
            <p>Continue to verify your email and set up your password.</p>
            <button
              type="button"
              className="primary-button full"
              disabled={busy}
              onClick={() => void confirmLink()}
            >
              Continue account setup
            </button>
          </>
        ) : null}
        {stage === "request" ? (
          <form onSubmit={requestRecovery}>
            <p>Enter the email address used for your workforce invitation.</p>
            <label>
              Email
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <button type="submit" className="primary-button full" disabled={busy}>
              Send setup email
            </button>
          </form>
        ) : null}
        {stage === "totp" ? (
          <form onSubmit={verifyAuthenticator}>
            <p>
              Verify the authenticator already enrolled for {verifiedEmail || "this account"} before
              changing its password.
            </p>
            <label>
              6-digit code
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </label>
            <button type="submit" className="primary-button full" disabled={busy}>
              Verify authenticator
            </button>
          </form>
        ) : null}
        {stage === "password" ? (
          <form onSubmit={savePassword}>
            <p>Choose a password for {verifiedEmail || "your verified account"}.</p>
            <p>
              Use at least 12 characters with uppercase and lowercase letters, a number, and a
              symbol.
            </p>
            <label>
              New password
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <label>
              Confirm password
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </label>
            <button type="submit" className="primary-button full" disabled={busy}>
              Save password
            </button>
          </form>
        ) : null}
        {stage === "saved" ? (
          <p>
            Sign in with your email and new password to continue. Any required authenticator
            verification follows sign-in.
          </p>
        ) : null}
        {notice ? <p role="status">{notice}</p> : null}
        {busy ? <p role="status">Please wait…</p> : null}
        {error ? (
          <p className="workflow-blocker" role="alert">
            {error}
          </p>
        ) : null}
        <button
          type="button"
          className="full"
          disabled={busy}
          onClick={() => void returnToSignIn()}
        >
          Back to sign-in
        </button>
      </section>
    </main>
  );
}
